import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CatalogTable from "../components/CatalogTable";
import ProjectModal from '../components/ProjectModal';
import { getCatalogs } from '../services/projectService';
import { Catalog } from "../interfaces/Catalog";

const CatalogManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null);
    const [catalogs, setCatalogs] = useState<Catalog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCatalogs = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getCatalogs();
            setCatalogs(response.data as Catalog[]);
        } catch (error) {
            setError("Erreur lors de la récupération des catalogues");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCatalogs();
    }, []);

    const handleCreateCatalog = () => {
        setSelectedCatalog(null);
        setIsModalOpen(true);
    };

    const handleEditCatalog = (catalog: Catalog) => {
        setSelectedCatalog(catalog);
        setIsModalOpen(true);
    };

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des catalogues
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez vos catalogues de produits et services
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateCatalog}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Créer un catalogue
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Contenu principal */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <CatalogTable
                                catalogs={catalogs}
                                onEditCatalogs={handleEditCatalog}
                                refreshCatalogs={fetchCatalogs}
                            />
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <ProjectModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchCatalogs();
                    }}
                    project={selectedCatalog}
                />
            </Box>
        </MainLayout>
    );
};

export default CatalogManagement;
